import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from 'recharts';
import { apiClient } from '../api/client';
import type { WpSite, BlockRegistryItem } from '@audit/shared-types';

interface BlockUsage {
  blockName: string;
  totalCount: number;
  pageCount: number;
}

type TypeFilter = 'all' | 'moj' | 'core' | 'other';

const FILTER_LABELS: Record<TypeFilter, string> = {
  all: 'All',
  moj: 'MoJ Custom',
  core: 'Core',
  other: 'Third-party',
};

const TYPE_BADGE: Record<'moj' | 'core' | 'other', string> = {
  moj: 'bg-purple-100 text-purple-700',
  core: 'bg-blue-100 text-blue-700',
  other: 'bg-gray-100 text-gray-600',
};

const TYPE_LABEL: Record<'moj' | 'core' | 'other', string> = {
  moj: 'MoJ',
  core: 'Core',
  other: 'Other',
};

export default function BlocksPage() {
  const [siteId, setSiteId] = useState<number | undefined>();
  const [aiInsights, setAiInsights] = useState<string | null>(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<TypeFilter>('all');

  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiClient.get<WpSite[]>('/sites').then((r) => r.data),
  });

  const { data = [], isLoading } = useQuery({
    queryKey: ['blocks', 'usage', siteId],
    queryFn: () =>
      apiClient
        .get<BlockUsage[]>('/blocks/usage', { params: siteId != null ? { siteId } : {} })
        .then((r) => r.data),
  });

  const { data: registry = [] } = useQuery({
    queryKey: ['blocks', 'registry'],
    queryFn: () => apiClient.get<BlockRegistryItem[]>('/blocks/registry').then((r) => r.data),
    staleTime: 5 * 60 * 1000,
  });

  const registryMap = new Map(registry.map((r) => [r.name, r]));

  const enrichedBlocks = data.map((block) => {
    const reg = registryMap.get(block.blockName);
    const rawType: 'moj' | 'core' | 'other' = block.blockName.startsWith('mojblocks/')
      ? 'moj'
      : block.blockName.startsWith('core/')
        ? 'core'
        : 'other';
    return {
      ...block,
      title: reg?.title || block.blockName.split('/').pop() || block.blockName,
      category: reg?.category || '',
      blockType: (reg?.blockType ?? rawType) as 'moj' | 'core' | 'other',
      deprecated: reg?.deprecated ?? false,
    };
  });

  const mojRegistryBlocks = registry.filter((r) => r.blockType === 'moj');
  const usedMojNames = new Set(
    enrichedBlocks.filter((b) => b.blockType === 'moj').map((b) => b.blockName),
  );
  const unusedMojBlocks = mojRegistryBlocks.filter((r) => !usedMojNames.has(r.name));

  const typeCounts: Record<TypeFilter, number> = {
    all: enrichedBlocks.length,
    moj: enrichedBlocks.filter((b) => b.blockType === 'moj').length,
    core: enrichedBlocks.filter((b) => b.blockType === 'core').length,
    other: enrichedBlocks.filter((b) => b.blockType === 'other').length,
  };

  const filteredBlocks =
    typeFilter === 'all' ? enrichedBlocks : enrichedBlocks.filter((b) => b.blockType === typeFilter);

  const chartData = enrichedBlocks.slice(0, 20).map((b) => ({
    name: b.title,
    uses: b.totalCount,
    moj: b.blockType === 'moj',
  }));

  const siteLabel = siteId
    ? (sites.find((s) => s.id === siteId)?.name ?? `site ${siteId}`)
    : 'all sites';

  async function getAiInsights() {
    setAiLoading(true);
    setAiError(null);
    setAiInsights(null);
    try {
      const res = await apiClient.post<{ analysis: string }>('/ai/block-trends', {
        ...(siteId != null ? { siteId } : {}),
      });
      setAiInsights(res.data.analysis);
    } catch {
      setAiError('AI unavailable — please try again.');
    } finally {
      setAiLoading(false);
    }
  }

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-2xl font-bold text-gray-900">Block Usage</h2>
        <div className="flex items-center gap-3">
          <select
            value={siteId ?? ''}
            onChange={(e) => {
              setSiteId(e.target.value ? Number(e.target.value) : undefined);
              setAiInsights(null);
            }}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 bg-white"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>{s.name}</option>
            ))}
          </select>
          <button
            onClick={() => void getAiInsights()}
            disabled={aiLoading || data.length === 0}
            className="text-sm bg-blue-600 text-white px-3 py-1.5 rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {aiLoading ? 'Analysing…' : '✨ AI Insights'}
          </button>
        </div>
      </div>

      {/* AI Insights panel */}
      {aiError && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          {aiError}
        </div>
      )}
      {aiInsights && (
        <div className="mb-6 p-5 bg-blue-50 border border-blue-200 rounded-xl">
          <p className="text-xs font-semibold text-blue-700 uppercase tracking-wide mb-2">
            ✨ AI Block Insights
          </p>
          <p className="text-sm text-blue-900">{aiInsights}</p>
        </div>
      )}

      {/* MoJ Block Adoption card */}
      {mojRegistryBlocks.length > 0 && (
        <div className="mb-6 bg-white border border-gray-200 rounded-xl p-5">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-gray-800">MoJ Custom Block Adoption</h3>
            <span className="text-sm font-semibold text-purple-700">
              {usedMojNames.size} / {mojRegistryBlocks.length} in use
            </span>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-2 mb-4">
            <div
              className="bg-purple-500 h-2 rounded-full transition-all"
              style={{ width: `${(usedMojNames.size / mojRegistryBlocks.length) * 100}%` }}
            />
          </div>
          {unusedMojBlocks.length > 0 && (
            <div>
              <p className="text-xs text-gray-500 mb-2">
                Not yet used on {siteLabel}:
              </p>
              <div className="flex flex-wrap gap-1.5">
                {unusedMojBlocks.map((b) => (
                  <span
                    key={b.name}
                    className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded"
                  >
                    {b.name.replace('mojblocks/', '')}
                  </span>
                ))}
              </div>
            </div>
          )}
          {unusedMojBlocks.length === 0 && (
            <p className="text-xs text-green-600 font-medium">All MoJ custom blocks are in use ✓</p>
          )}
        </div>
      )}

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading block data…</p>
      ) : (
        <>
          {/* Chart */}
          <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6">
            <h3 className="text-sm font-semibold text-gray-700 mb-4">
              Top 20 Blocks — {siteLabel}
            </h3>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={chartData} layout="vertical" margin={{ left: 80 }}>
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 12 }} />
                <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={80} />
                <Tooltip />
                <Bar dataKey="uses" radius={[0, 4, 4, 0]}>
                  {chartData.map((entry, index) => (
                    <Cell key={index} fill={entry.moj ? '#7c3aed' : '#2563eb'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            <p className="text-xs text-gray-400 mt-3 flex items-center gap-4">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-purple-600" />
                MoJ custom block
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 rounded-sm bg-blue-600" />
                WordPress core block
              </span>
            </p>
          </div>

          {/* Filter tabs */}
          <div className="flex gap-1.5 mb-3">
            {(['all', 'moj', 'core', 'other'] as TypeFilter[]).map((f) => (
              <button
                key={f}
                onClick={() => setTypeFilter(f)}
                className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                  typeFilter === f
                    ? 'bg-gray-900 text-white'
                    : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                }`}
              >
                {FILTER_LABELS[f]}{' '}
                <span className="opacity-60">({typeCounts[f]})</span>
              </button>
            ))}
          </div>

          {/* Table */}
          <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase bg-gray-50">
                  <th className="px-6 py-3">Block</th>
                  <th className="px-6 py-3">Type</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3">Total Uses</th>
                  <th className="px-6 py-3">Pages</th>
                </tr>
              </thead>
              <tbody>
                {filteredBlocks.map((block) => (
                  <tr
                    key={block.blockName}
                    className={`border-t border-gray-100 hover:bg-gray-50 ${block.deprecated ? 'bg-amber-50' : ''}`}
                  >
                    <td className="px-6 py-3">
                      <Link
                        to={`/blocks/details?blockName=${encodeURIComponent(block.blockName)}${siteId != null ? `&siteId=${siteId}` : ''}`}
                        className="inline-flex items-center gap-2 font-medium text-gray-800 text-xs hover:text-brand-700"
                      >
                        <span>{block.title}</span>
                        <span className="text-[11px] text-brand-600">View details →</span>
                      </Link>
                      {block.deprecated && (
                        <span className="ml-2 text-amber-600 text-xs">⚠ deprecated</span>
                      )}
                      <div className="font-mono text-xs text-gray-400 mt-0.5">{block.blockName}</div>
                    </td>
                    <td className="px-6 py-3">
                      <span
                        className={`text-xs px-2 py-0.5 rounded-full font-medium ${TYPE_BADGE[block.blockType]}`}
                      >
                        {TYPE_LABEL[block.blockType]}
                      </span>
                    </td>
                    <td className="px-6 py-3 text-xs text-gray-500 capitalize">
                      {block.category || '—'}
                    </td>
                    <td className="px-6 py-3 text-gray-900">{block.totalCount}</td>
                    <td className="px-6 py-3 text-gray-500">{block.pageCount}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}


